import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, formatApiError } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock } from "lucide-react";
import SEO from "../components/SEO";

export default function AdminLoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Admin Login | 5999Cargo" description="Admin access only." />
      <section className="min-h-[80vh] flex items-center bg-[#F8F9FA]" data-testid="admin-login-page">
        <div className="max-w-md mx-auto w-full px-4">
          <div className="bg-white border border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#082c50] flex items-center justify-center">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-chivo font-black text-xl text-[#082c50]">Backoffice login</div>
                <div className="text-xs text-gray-500">5999Cargo Group Europe</div>
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
              <div>
                <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">
                  E-mailadres
                </Label>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 rounded-xl border-gray-300"
                  placeholder="naam@5999cargo.nl"
                />
              </div>
              <div>
                <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">
                  Wachtwoord
                </Label>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 rounded-xl border-gray-300"
                />
              </div>

              {error && (
                <div
                  data-testid="admin-login-error"
                  className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm"
                >
                  {error}
                </div>
              )}

              <Button
                data-testid="admin-login-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-sm py-6"
              >
                {loading ? "Inloggen..." : "Inloggen"}
              </Button>

              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  data-testid="admin-forgot-password-link"
                  onClick={() => navigate("/admin/forgot-password")}
                  className="text-xs text-[#1a5193] hover:underline font-chivo tracking-wide"
                >
                  Wachtwoord vergeten?
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center pt-2">
                Beveiligde zone. Na 5 mislukte pogingen wordt het account 15 minuten geblokkeerd.
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
